import { createSlice } from "@reduxjs/toolkit";
import BigNumber from "bignumber.js";
import nicknamesSanitization, {
  addToBannedNames,
  loadInitListOfBannedNames,
} from "@axvdex/utils/nicknamesSanitization";
import state from "./initialState";
//import { connectWalletWithDispatch, updateTokenBalance } from "./walletThunks";

export const walletSlice = createSlice({
  name: "wallet",
  initialState: state,
  reducers: {
    clearWallet: state => {
      state.walletInfo = {
        isConnected: false,
        walletAddress: "",
        walletOwnerName: "",
        connectionStatus: "",
        nativeCoin: "",
        walletConnectAccountNumber: null,
        walletConnectSequence: null,
        walletSignerType: "",
        useSolanaAdapter: false,
      };

      state.assetBalances = {};
      state.poolsLpBalance = {};
      state.user = {
        favoriteSwaps: [],
        favoritePools: [],
        favoriteFarms: [],
        priceWatch: [],
        poolsWithBalance: [],
        farmsWithBalance: [],
        swapLogs: [],
        myAssets: [],
        favoriteAssets: [],
        isUserDetailsLoad: false,
        grvt8Balance: null,
        myDashboardGrid: null,
        myGlobalSettings: null,
        myToastMessages: [],
        vestingLockedAmount: null,
        myAssetUnmints: {},
        mySoftLockups: {},
        notifications: [],
        surveys: null,
      };

      state.toast = null;
      state.isLoadingWallet = false;
    },
    setLoadingWallet: (state, { payload }) => {
      state.isLoadingWallet = payload;
    },
    initWalletInfo: (state, action) => {
      const { wallet, assetBalances, useSolanaAdapter } = action.payload;
      state.walletInfo = {
        isConnected: true,
        walletAddress: wallet.address,
        walletOwnerName: wallet.name,
        walletConnectAccountNumber: wallet.connectAccountNumber,
        walletConnectSequence: wallet.connectSequence,
        walletSignerType: wallet.signerType,
        nativeCoin: process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM as string,
        connectionStatus: "CONNECTED",
        useSolanaAdapter: useSolanaAdapter
      };
      // if there is a vesting locked amount, subtract it from the native denom balance...
      if (state.user.vestingLockedAmount && assetBalances[process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM]) {
        const newBalance = BigNumber(assetBalances[process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM]).minus(
          state.user.vestingLockedAmount
        );
        if (newBalance.gte(0)) {
          assetBalances[process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM] = newBalance.toString(10);
        } else {
          assetBalances[process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM] = "0";
        }
      }
      state.assetBalances = { ...state.assetBalances, ...assetBalances };
      state.isLoadingWallet = false;
    },
    setWalletInfo: (_state, _) => {},
    updateConnectionStatus: (state, { payload }) => {
      const { isConnected, walletAddress, assetBalances, connectionStatus } = payload;
      state.walletInfo = {
        isConnected,
        walletAddress,
        walletOwnerName: state.walletInfo.walletOwnerName,
        connectionStatus,
        nativeCoin: state.walletInfo.nativeCoin,
        walletConnectAccountNumber: state.walletInfo.walletConnectAccountNumber,
        walletConnectSequence: state.walletInfo.walletConnectSequence,
        walletSignerType: state.walletInfo.walletSignerType,
        // TODO: Solana adapter is not implemented yet
        useSolanaAdapter: false
      };
      state.assetBalances = { ...state.assetBalances, ...assetBalances };
      state.isLoadingWallet = false;
    },
    updateAssets: (state, action) => {
      state.assets = { ...state.assets, ...action.payload };
    },
    updateWsAssetFields: (state, action) => {
      // only update the fields if the item already exists on state
      if (state.assets[action.payload.id]) {
        state.assets = {
          ...state.assets,
          [action.payload.id]: {
            ...state.assets[action.payload.id],
            price: action.payload.price,
          },
        };
      }
    },
    updateWsContractFields: (state, action) => {
      // only update the fields if the item already exists on state
      if (state.contracts[action.payload.label]) {
        if (action.payload.extraFields)
          state.contracts = {
            ...state.contracts,
            [action.payload.label]: {
              ...state.contracts[action.payload.label],
              extraFields: action.payload.extraFields,
            },
          };
      }
    },
    updateWsFarmFields: (state, action) => {
      state.farms = {
        ...state.farms,
        [action.payload.address]: {
          ...state.farms[action.payload.address],
          tvl: action.payload.tvl,
        },
      };
    },
    updateAssetBalances: (state, action) => {
      // if there is a vesting locked amount, subtract it from the native denom balance...
      if (state.user.vestingLockedAmount && action.payload[process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM]) {
        const newBalance = BigNumber(action.payload[process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM]).minus(
          state.user.vestingLockedAmount
        );
        if (newBalance.gte(0)) {
          action.payload[process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM] = newBalance.toString(10);
        } else {
          action.payload[process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM] = "0";
        }
      }
      state.assetBalances = { ...state.assetBalances, ...action.payload };
    },
    updateAssetsUnmints: (state, action) => {
      state.user.myAssetUnmints = { ...state.user.myAssetUnmints, ...action.payload };
    },
    updateMySoftLockups: (state, action) => {
      state.user.mySoftLockups = { ...state.user.mySoftLockups, ...action.payload };
    },
    updatePools: (state, action) => {
      state.pools = { ...state.pools, ...action.payload };
    },
    updateFarms: (state, action) => {
      state.farms = { ...state.farms, ...action.payload };
    },
    updatePoolsLpBalance: (state, action) => {
      state.poolsLpBalance = { ...state.poolsLpBalance, ...action.payload };
    },
    updateFarmsLpBalance: (state, action) => {
      state.farmsLpBalance = { ...state.farmsLpBalance, ...action.payload };
    },
    updateWsPoolFields: (state, action) => {
      // only update the fields if the item already exits on state
      if (state.pools[action.payload.address]) {
        state.pools = {
          ...state.pools,
          [action.payload.address]: {
            ...state.pools[action.payload.address],
            poolAssets: action.payload.poolAssetsAmounts
              ? state.pools[action.payload.address].poolAssets.map((poolAsset, i) => {
                  return {
                    info: poolAsset.info,
                    amount: action.payload.poolAssetsAmounts[i],
                  };
                })
              : state.pools[action.payload.address].poolAssets,
            hybridRatioDetails: {
              ...state.pools[action.payload.address].hybridRatioDetails,
              ratio: action.payload.ratio,
            },
          },
        };
      }
    },
    setPoolInfo: (state, action) => {
      state.pools = {
        ...state.pools,
        [action.payload.poolAddress]: {
          ...state.pools[action.payload.poolAddress],
          poolAssets: state.pools[action.payload.poolAddress].poolAssets.map((asset, i) => {
            return {
              ...asset,
              amount: action.payload.amounts[i],
            };
          }),
          total_share: action.payload.total_share,
        },
      };
    },
    addFavouritePool: (state, { payload }) => {
      state.user.favoritePools = state.user.favoritePools.includes(payload.favoritePool)
        ? [...state.user.favoritePools]
        : [...state.user.favoritePools, payload.favoritePool];
    },
    deleteFavouritePool: (state, { payload }) => {
      state.user.favoritePools = state.user.favoritePools.filter(item => item !== payload.favoritePool);
    },
    addFavouriteFarm: (state, { payload }) => {
      state.user.favoriteFarms = state.user.favoriteFarms.includes(payload.favoriteFarm)
        ? [...state.user.favoriteFarms]
        : [...state.user.favoriteFarms, payload.favoriteFarm];
    },
    deleteFavouriteFarm: (state, { payload }) => {
      state.user.favoriteFarms = state.user.favoriteFarms.filter(item => item !== payload.favoriteFarm);
    },
    updateContracts: (state, action) => {
      // this is called on the initial load of contracts
      state.contracts = { ...state.contracts, ...action.payload };
    },
    updateChains: (state, action) => {
      state.chains = action.payload;
    },
    updateStatusCounters: (state, action) => {
      state.statusCounters = action.payload;
    },
    setStateUser: (state, { payload }) => {
      state.user = payload;
    },
    setStateBalances: (state, { payload }) => {
      state.assetBalances = payload;
    },
    setStateLpBalances: (state, { payload }) => {
      state.poolsLpBalance = payload;
    },
    setStateFarmBalances: (state, { payload }) => {
      state.farmsLpBalance = payload;
    },
    updatingUser: (state, { payload }) => {
      state.updatingUser = payload;
    },
    updateUser: (state, { payload }) => {
      const user = payload;
      const balanceOfUserPools = user.poolsWithBalance.reduce((acc, item) => {
        return { ...acc, [item.contract_addr]: item.balance || "0" };
      }, {});
      const transformedUser = { ...user, isUserDetailsLoad: !!user.address };
      delete transformedUser.address;

      state.assetBalances = { ...state.assetBalances, ...balanceOfUserPools };
      state.user = { ...state.user, ...transformedUser };
      // add globalSettings field and poolsWithBalance if its undefined from the API so the app knows things came from API but there are no configured globalSettings
      state.user.myGlobalSettings = state.user.myGlobalSettings ?? {};
      state.user.poolsWithBalance = state.user.poolsWithBalance ?? [];
      state.updatingUser = false;
    },
    setUserSwapLogs: (state, { payload }) => {
      state.user.swapLogs = payload;
    },
    updateSwapFavourites: (state, action) => {
      state.user.favoriteSwaps = action.payload;
    },
    updateFavouriteAssets: (state, action) => {
      state.user.favoriteAssets = action.payload;
    },
    updateMyAssets: (state, action) => {
      state.user.myAssets = action.payload;
    },
    updatePriceWatch: (state, { payload }) => {
      state.user.priceWatch = payload;
    },
    setGRVT8Balance: (state, { payload }) => {
      state.user.grvt8Balance = payload;
    },
    sendToast: (state, action) => {
      state.toast = action.payload;
    },
    updateToastMessages: (state, action) => {
      state.user.myToastMessages = action.payload;
    },
    setMyDashboardGrid: (state, action) => {
      state.user.myDashboardGrid = action.payload;
    },
    setMyGlobalSettings: (state, action) => {
      state.user.myGlobalSettings = action.payload;
    },
    setVestingLockedAmount: (state, action) => {
      state.user.vestingLockedAmount = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  clearWallet,
  initWalletInfo,
  setWalletInfo,
  updateConnectionStatus,
  updateAssetBalances,
  updateAssetsUnmints,
  updateMySoftLockups,
  updateUser,
  updateSwapFavourites,
  updateFavouriteAssets,
  setStateUser,
  setStateBalances,
  setStateLpBalances,
  setStateFarmBalances,
  updateAssets,
  updateMyAssets,
  updatePools,
  updateFarms,
  updatePoolsLpBalance,
  updateFarmsLpBalance,
  updateWsPoolFields,
  updateWsAssetFields,
  updateWsContractFields,
  updateWsFarmFields,
  updateContracts,
  updateChains,
  updateStatusCounters,
  setPoolInfo,
  addFavouritePool,
  deleteFavouritePool,
  addFavouriteFarm,
  deleteFavouriteFarm,
  sendToast,
  updateToastMessages,
  setUserSwapLogs,
  setLoadingWallet,
  updatePriceWatch,
  setGRVT8Balance,
  updatingUser,
  setMyDashboardGrid,
  setMyGlobalSettings,
  setVestingLockedAmount,
} = walletSlice.actions;

export default walletSlice.reducer;
